import { useEffect, useMemo, useState } from "react";
import {
  fetchAcademyRewardOrdersAPI,
  fetchAcademyRewardProductsAPI,
  fetchRewardAccountAPI,
  refreshRewardBalanceAPI,
  saveRewardAccountAPI,
  selectAcademyRewardProductsAPI,
  forgetRewardOpen,
  fetchRewardRuleListAPI,
  saveRewardRuleListAPI,
} from "./rewardAPI";
import "./Reward.css";

// 학원 관리 > 포인트 · 기프티콘(2026-10-04).
// 학원이 자기 기프티쇼 계정을 잇고, 본사가 허용한 상품 중 학생 상점에 올릴 것을 고른다.
// 쿠폰값은 이 학원의 기프티쇼 비즈머니에서 나간다. 학생에게는 정가만큼 포인트가 든다(1원 = 1P).

const formatNumber = (value) => (value ?? 0).toLocaleString("ko-KR");
const formatDate = (value) => (value ? new Date(value).toLocaleDateString("ko-KR") : "");

const groupByBrand = (productList) => {
  const brandMap = new Map();
  productList.forEach((product) => {
    if (!brandMap.has(product.brand_name)) brandMap.set(product.brand_name, []);
    brandMap.get(product.brand_name).push(product);
  });
  return Array.from(brandMap.entries());
};

const AccountCard = () => {
  const [account, setAccount] = useState(null);
  const [isMock, setIsMock] = useState(false);
  const [form, setForm] = useState({ member_id: "", auth_code: "", auth_token: "", callback_number: "", is_active: false });
  const [balance, setBalance] = useState(null);
  const [message, setMessage] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    fetchRewardAccountAPI()
      .then((data) => {
        setAccount(data.account);
        setIsMock(data.is_mock);
        setForm({
          member_id: data.account.member_id,
          auth_code: data.account.auth_code,
          auth_token: data.account.auth_token,
          callback_number: data.account.callback_number,
          is_active: data.account.is_active,
        });
        setBalance(data.account.last_balance);
      })
      .catch(() => setMessage("계정 정보를 불러오지 못했어요."));
  }, []);

  const save = async () => {
    setIsBusy(true);
    setMessage("");
    try {
      const data = await saveRewardAccountAPI(form);
      setAccount(data.account);
      setForm((previous) => ({
        ...previous,
        auth_code: data.account.auth_code,
        auth_token: data.account.auth_token,
        is_active: data.account.is_active,
      }));
      forgetRewardOpen();
      setMessage("저장했어요.");
    } catch {
      setMessage("저장하지 못했어요.");
    }
    setIsBusy(false);
  };

  const refresh = async () => {
    setIsBusy(true);
    setMessage("");
    try {
      const data = await refreshRewardBalanceAPI();
      setBalance(data.balance);
      setMessage("연결을 확인했어요.");
    } catch (error) {
      setMessage(error?.response?.data?.error || "연결을 확인하지 못했어요.");
    }
    setIsBusy(false);
  };

  const update = (key) => (event) => setForm((previous) => ({ ...previous, [key]: event.target.value }));

  return (
    <div className="reward-card">
      <div className="reward-card__title">
        기프티쇼 비즈 연결
        <span>
          {isMock && <span className="reward-badge reward-badge--mock">모의 연결</span>}{" "}
          <span className={`reward-badge ${account?.is_connected ? "reward-badge--on" : "reward-badge--off"}`}>
            {account?.is_connected ? "연결됨" : "연결 안 됨"}
          </span>
        </span>
      </div>
      <div className="reward-form">
        <label>
          회원 ID
          <input value={form.member_id} onChange={update("member_id")} autoComplete="off" />
        </label>
        <label>
          발신번호
          <input value={form.callback_number} onChange={update("callback_number")} inputMode="numeric" />
        </label>
        <label>
          API 인증키
          <input value={form.auth_code} onChange={update("auth_code")} autoComplete="off" />
        </label>
        <label>
          API 인증토큰
          <input value={form.auth_token} onChange={update("auth_token")} autoComplete="off" />
        </label>
      </div>
      <label className="reward-active">
        <input
          type="checkbox"
          checked={Boolean(form.is_active)}
          onChange={(event) => setForm((previous) => ({ ...previous, is_active: event.target.checked }))}
        />
        활성화 (학생 포인트 · 상점 열기)
      </label>
      <div className="reward-actions">
        <button type="button" className="reward-button" onClick={save} disabled={isBusy}>
          저장
        </button>
        <button type="button" className="reward-button reward-button--ghost" onClick={refresh} disabled={isBusy}>
          연결 확인 · 잔액 새로고침
        </button>
        {balance !== null && balance !== undefined && (
          <span className="reward-balance">
            비즈머니 {formatNumber(balance)}원
            {account?.last_balance_checked_at && <small>{formatDate(account.last_balance_checked_at)}</small>}
          </span>
        )}
        {message && <span className="reward-message">{message}</span>}
      </div>
    </div>
  );
};

// 과제를 끝낸 학생에게 유형마다 '몇 점 이상이면 몇 P' 를 준다(2026-10-06 대표). 출제 창이 이 값으로 채우고,
// 출제 때 바꾼 값(이벤트)은 그 출제에만 쓰인다. 수학 계정은 수학 유형만 나온다(서버가 고른다).
const RuleCard = () => {
  const [ruleList, setRuleList] = useState([]);
  // 수학 풀이 사진을 올리면 더 주는 포인트(수학 계정만 서버가 내려준다)
  const [workPhotoBonus, setWorkPhotoBonus] = useState(undefined);
  const [message, setMessage] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const applyRules = (data) => {
    setRuleList(data.rule_list);
    setWorkPhotoBonus(data.work_photo_bonus);
  };

  useEffect(() => {
    fetchRewardRuleListAPI()
      .then(applyRules)
      .catch(() => setMessage("불러오지 못했어요."));
  }, []);

  const change = (type, key, text, max) =>
    setRuleList((previous) =>
      previous.map((rule) =>
        rule.type === type ? { ...rule, [key]: text === "" ? "" : Math.min(max, Math.max(0, Number(text))) } : rule
      )
    );

  const save = async () => {
    setIsBusy(true);
    setMessage("");
    try {
      applyRules(await saveRewardRuleListAPI(ruleList, workPhotoBonus === "" ? 0 : workPhotoBonus));
      setMessage("저장했어요.");
    } catch (error) {
      setMessage(error?.response?.data?.error || "저장하지 못했어요.");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="reward-card">
      <div className="reward-card__title">과제 포인트 기본값</div>
      <div className="reward-rule-list">
        {ruleList.map((rule) => (
          <div key={rule.type} className="reward-rule">
            <span className="reward-rule__name">{rule.name}</span>
            <input
              type="number"
              min={0}
              max={100}
              step={10}
              inputMode="numeric"
              value={rule.min_score}
              onChange={(event) => change(rule.type, "min_score", event.target.value, 100)}
            />
            <span>점 이상</span>
            <input
              type="number"
              min={0}
              step={10}
              inputMode="numeric"
              value={rule.reward_points}
              onChange={(event) => change(rule.type, "reward_points", event.target.value, 100000)}
            />
            <span>P</span>
          </div>
        ))}
      </div>
      {workPhotoBonus !== undefined && (
        <div className="reward-rule reward-rule--bonus">
          <span className="reward-rule__name">풀이 사진 올리면</span>
          <input
            type="number"
            min={0}
            step={10}
            inputMode="numeric"
            value={workPhotoBonus}
            onChange={(event) =>
              setWorkPhotoBonus(event.target.value === "" ? "" : Math.min(100000, Math.max(0, Number(event.target.value))))
            }
          />
          <span>P 더</span>
        </div>
      )}
      <div className="reward-actions">
        <button type="button" className="reward-button" onClick={save} disabled={isBusy || ruleList.length === 0}>
          저장
        </button>
        {message && <span className="reward-message">{message}</span>}
      </div>
    </div>
  );
};

const ProductCard = () => {
  const [productList, setProductList] = useState([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchAcademyRewardProductsAPI()
      .then((data) => setProductList(data.product_list))
      .catch(() => setMessage("상품을 불러오지 못했어요."));
  }, []);

  const brandList = useMemo(() => groupByBrand(productList), [productList]);
  const selectedCount = productList.filter((product) => product.is_selected).length;

  const toggle = async (idList, isSelected) => {
    try {
      const data = await selectAcademyRewardProductsAPI(idList, isSelected);
      setProductList(data.product_list);
    } catch {
      setMessage("저장하지 못했어요.");
    }
  };

  return (
    <div className="reward-card">
      <div className="reward-card__title">
        학생 상점 상품
        <em>{selectedCount}개 올림</em>
      </div>
      {message && <div className="reward-message">{message}</div>}
      {brandList.length === 0 && <div className="reward-empty">고를 수 있는 상품이 아직 없어요.</div>}
      {brandList.map(([brandName, list]) => {
        const isAllSelected = list.every((product) => product.is_selected);
        return (
          <div className="reward-brand" key={brandName}>
            <div className="reward-brand__head">
              <b>{brandName}</b>
              <button type="button" onClick={() => toggle(list.map((product) => product.id), !isAllSelected)}>
                {isAllSelected ? "모두 내리기" : "모두 올리기"}
              </button>
            </div>
            <div className="reward-product-grid">
              {list.map((product) => (
                <button
                  type="button"
                  key={product.id}
                  className="reward-product"
                  aria-pressed={product.is_selected}
                  onClick={() => toggle([product.id], !product.is_selected)}
                >
                  <span className="reward-check">{product.is_selected ? "✓" : ""}</span>
                  <span className="reward-product__name">{product.goods_name}</span>
                  <span className="reward-product__price">{formatNumber(product.point_price)}P</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const OrderCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAcademyRewardOrdersAPI().then(setData).catch(() => setData({ order_list: [], issued_this_month: 0 }));
  }, []);

  if (!data) return null;
  return (
    <div className="reward-card">
      <div className="reward-card__title">
        교환 내역
        <em>이번 달 {formatNumber(data.issued_this_month)}P</em>
      </div>
      {data.order_list.length === 0 ? (
        <div className="reward-empty">아직 교환한 학생이 없어요.</div>
      ) : (
        <table className="reward-table">
          <thead>
            <tr>
              <th>날짜</th>
              <th>학생</th>
              <th>상품</th>
              <th>상태</th>
              <th style={{ textAlign: "right" }}>포인트</th>
            </tr>
          </thead>
          <tbody>
            {data.order_list.map((order) => (
              <tr key={order.id}>
                <td>{formatDate(order.created_at)}</td>
                <td>{order.student_name}</td>
                <td>
                  {order.product.brand_name} {order.product.goods_name}
                </td>
                <td>{order.status_label}</td>
                <td className="reward-table__num">{formatNumber(order.points_used)}P</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const RewardSettingsPage = () => (
  <div className="reward-page">
    <AccountCard />
    <RuleCard />
    <ProductCard />
    <OrderCard />
  </div>
);

export default RewardSettingsPage;
